import type { HouseRuleSection } from "./house-rules";

/**
 * Guest Release of Liability, Assumption of Risk and Indemnity Agreement.
 *
 * DRAFT LANGUAGE. Not reviewed by a licensed Hawaii attorney. It must be
 * reviewed by Hawaii counsel before it is relied on, and nothing here is a
 * promise that any claim is barred.
 *
 * Why this exists alongside the Liability Terms and the water waiver:
 *
 *   - The Liability Terms are accepted once, by the person who books, at
 *     checkout. A release binds only the person who signs it, so it cannot
 *     cover the other adults in the party.
 *   - The water waiver covers the optional equipment and is limited by HRS
 *     s 663-1.54 to the risks inherent in those activities.
 *
 * This document is the premises release: every adult on the booking signs it
 * for themselves, online, before the entry codes are released.
 *
 * Drafting choices, all for enforceability rather than tone:
 *
 *   1. The release is written "to the fullest extent permitted by law" and
 *      names ordinary negligence expressly and conspicuously, which Hawaii
 *      courts look for before enforcing a release of a party's own negligence.
 *   2. It does NOT claim to release gross negligence or wilful or wanton
 *      misconduct. No court enforces that, and an overreaching release is the
 *      easiest kind for a court to throw out whole.
 *   3. A severability clause keeps the rest standing if any part is struck.
 *   4. Minors: Hawaii courts do not enforce a parent's pre-injury release of a
 *      child's own claims, so the parent does not purport to waive them. The
 *      parent releases their OWN claims arising from the child's injury,
 *      accepts supervision, and indemnifies to the extent Hawaii law allows.
 *   5. The risk list is specific to this property, because a release of risks
 *      the signer was told about is far stronger than a release of everything.
 */

/**
 * Stays that check in on or after this date require the Guest Release; stays
 * that check in before it never do, whenever they were booked. Set on
 * 2026-09-29 at the owner's request: nothing changes for the next two months
 * of stays on either home.
 */
export const GUEST_RELEASE_REQUIRED_FROM = "2026-11-29";

/** Whether a stay checking in on this date (YYYY-MM-DD) requires the Guest Release. */
export function guestReleaseRequired(startDate: string | Date | null | undefined): boolean {
  if (!startDate) return false;
  const ymd = startDate instanceof Date ? startDate.toISOString().slice(0, 10) : String(startDate).slice(0, 10);
  return ymd >= GUEST_RELEASE_REQUIRED_FROM;
}

/** "November 29, 2026", for guest-facing text. */
export const GUEST_RELEASE_REQUIRED_FROM_LABEL = new Date(`${GUEST_RELEASE_REQUIRED_FROM}T12:00:00Z`).toLocaleDateString("en-US", {
  month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
});

export const GUEST_RELEASE_TITLE = "Guest Release of Liability, Assumption of Risk and Indemnity Agreement";

export const GUEST_RELEASE_REVISION = "2026-09-29";

export const GUEST_RELEASE_SECTIONS: HouseRuleSection[] = [
  {
    anchor: "read-first",
    heading: "Please read this carefully",
    intro:
      "THIS IS A RELEASE OF LIABILITY AND A WAIVER OF CERTAIN LEGAL RIGHTS, INCLUDING THE RIGHT TO SUE FOR INJURY OR DEATH CAUSED BY ORDINARY NEGLIGENCE. BY SIGNING IT YOU GIVE UP SUBSTANTIAL RIGHTS. READ IT BEFORE YOU SIGN.",
    items: [
      "Every adult (18 or older) staying at or visiting the property during the booking signs this agreement for themselves. Nobody can sign it on another adult's behalf.",
      "A parent or legal guardian adds each child under 18 in their care.",
      "The entry codes for the house are released once everyone on the booking has signed.",
      "If you do not wish to sign, contact us before your arrival to discuss your booking.",
    ],
  },
  {
    anchor: "definitions",
    heading: "Who this agreement protects",
    items: [
      "\"Owner Parties\" means the owners of the property, the business that operates Vacation Rental Oahu and the property, and each of their members, managers, officers, employees, agents, property managers, cleaners, contractors, successors and assigns.",
      "\"The Stay\" means your booking, your presence at the property and its grounds, and everything you do there, including use of the house, its furnishings, appliances, amenities and equipment, and access to the shoreline and the ocean from the property.",
      "\"Your Party\" means everyone on your booking and anyone you invite or allow onto the property during the Stay.",
    ],
  },
  {
    anchor: "risks",
    heading: "Risks at this property",
    intro:
      "This is an oceanfront home in a rural coastal setting. The Stay involves risks, including but not limited to:",
    items: [
      "Slips, trips and falls on wet, sandy, uneven or slippery surfaces, including floors, bathrooms and showers, stairs, decks, lanai, lawn, paths, rocks and the seawall.",
      "Falls from heights, including changes in level without rails or barriers, and the upper bunks of bunk beds.",
      "Drowning and near-drowning. The ocean is directly accessible from the property, with no lifeguard, fence or barrier. Currents, rip currents, surge, shore break, reef, rocks and marine life can cause serious injury or death, and conditions change quickly and without warning.",
      "Burns, cuts and other injuries from kitchen appliances, grills, hot water, glass, tools and household items.",
      "Injury from furniture, fixtures, doors, windows and equipment, whether or not in apparent good condition.",
      "Insects, centipedes, spiders, geckos and other wildlife, and bites, stings and allergic reactions.",
      "Weather and natural events, including storms, high surf, flooding, falling branches and coconuts, tsunami and power interruptions.",
      "Delay in emergency response and distance from hospitals, because of the rural location.",
      "The acts or omissions of other guests, visitors and members of Your Party.",
      "Illness, injury, permanent disability, loss or damage to property, and death.",
    ],
    note:
      "This list describes the kinds of risk involved. It is not complete, and the Stay may involve risks not listed here.",
  },
  {
    anchor: "assumption",
    heading: "Assumption of risk",
    items: [
      "I have read the risks described above. I understand them, and I understand there may be others.",
      "I choose to take part in the Stay voluntarily, and I KNOWINGLY AND VOLUNTARILY ASSUME ALL RISKS of the Stay, both known and unknown, to the fullest extent permitted by law.",
      "Entering the ocean, using any equipment, and drinking alcohol are my own decisions, made at my own risk.",
    ],
  },
  {
    anchor: "release",
    heading: "Release of liability and covenant not to sue",
    items: [
      "TO THE FULLEST EXTENT PERMITTED BY THE LAW OF THE STATE OF HAWAII, I RELEASE, WAIVE, DISCHARGE AND COVENANT NOT TO SUE THE OWNER PARTIES FROM ANY AND ALL CLAIMS, DEMANDS, LIABILITIES, LOSSES AND CAUSES OF ACTION OF ANY KIND for bodily injury, illness, permanent disability, death, and loss of or damage to property, arising out of or in any way related to the Stay.",
      "THIS RELEASE INCLUDES CLAIMS ARISING FROM THE ORDINARY NEGLIGENCE OF THE OWNER PARTIES, to the fullest extent permitted by law.",
      "This release binds me, my heirs, next of kin, executors, administrators, personal representatives and assigns, to the fullest extent permitted by law, including as to any claim for wrongful death.",
      "This release does not apply to injury or loss caused by the gross negligence, or the wilful or wanton misconduct, of an Owner Party, or to any claim that the law does not allow to be released.",
    ],
  },
  {
    anchor: "indemnity",
    heading: "Indemnity",
    items: [
      "To the fullest extent permitted by law, I agree to indemnify, defend and hold harmless the Owner Parties from and against any and all claims, demands, losses, damages, judgments, costs and expenses, including reasonable attorneys' fees, brought by anyone (including any member of Your Party or any child in my care) arising out of or related to my acts or omissions during the Stay, or those of any person I invited or allowed onto the property.",
      "This indemnity does not apply to the extent a loss is caused by the gross negligence, or the wilful or wanton misconduct, of an Owner Party.",
    ],
  },
  {
    anchor: "children",
    heading: "Children under 18",
    intro:
      "This property is not childproofed. It has open ocean access and changes in level without rails.",
    items: [
      "If I sign for a child, I confirm that I am that child's parent or legal guardian, that I have read this agreement on their behalf, and that I will supervise them at all times, and in particular near the ocean and any edge or height.",
      "I release, on my own behalf, any claim I have or may have, including for medical expenses, loss of services or emotional distress, arising from injury to or the death of a child in my care during the Stay, to the fullest extent permitted by law.",
      "I understand that Hawaii law may not allow a parent to release a child's own claims before an injury. Nothing in this agreement is intended to release a claim that belongs to a child where the law does not permit it. My indemnity above applies to claims brought on behalf of a child in my care to the extent Hawaii law allows.",
    ],
  },
  {
    anchor: "responsibilities",
    heading: "My responsibilities",
    items: [
      "I will follow the House Rules, including the occupancy limit.",
      "I will use reasonable care for my own safety and the safety of Your Party, and I will tell every member of Your Party about the risks in this agreement.",
      "I will not enter the ocean when conditions are unsafe, and I will never swim alone.",
      "I will report any hazard, damage or malfunction to the host promptly, and stop using anything that appears unsafe.",
    ],
  },
  {
    anchor: "medical-insurance",
    heading: "Emergencies, medical care and insurance",
    items: [
      "In an emergency, I authorize the Owner Parties to call emergency services for me or Your Party. Any medical treatment and transport are at my own expense.",
      "The Owner Parties do not provide health, accident, travel or personal property insurance for guests. I am responsible for my own insurance.",
    ],
  },
  {
    anchor: "general",
    heading: "General terms",
    items: [
      "This agreement is governed by the law of the State of Hawaii. Any dispute about it or the Stay will be brought only in the state or federal courts located on Oahu, Hawaii.",
      "This agreement is intended to be as broad and inclusive as Hawaii law permits. If any part of it is held invalid or unenforceable, that part will be enforced to the maximum extent permitted and the rest of the agreement will remain in full force and effect.",
      "This agreement is in addition to the House Rules, the Liability Terms and, if signed, the Water Activity Waiver. It does not limit anything in them.",
      "My typed name below is my electronic signature and has the same effect as a handwritten signature. The date, time and exact text I signed are recorded and kept.",
    ],
  },
  {
    anchor: "acknowledgment",
    heading: "My acknowledgment",
    items: [
      "I am 18 or older and legally able to sign this agreement.",
      "I have read this entire agreement, I understand it, and I understand that by signing it I am giving up substantial legal rights, including the right to sue the Owner Parties for ordinary negligence.",
      "I sign it freely and voluntarily, without any inducement.",
    ],
    note:
      "Draft language. Not reviewed by a licensed Hawaii attorney, and no assurance is given that it bars or limits any particular claim.",
  },
];

/** Flatten to the exact plain text stored on the published version. */
export function renderGuestRelease(
  sections: HouseRuleSection[] = GUEST_RELEASE_SECTIONS,
): string {
  const parts: string[] = [GUEST_RELEASE_TITLE.toUpperCase(), ""];
  for (const s of sections) {
    parts.push(s.heading.toUpperCase());
    if (s.intro) parts.push(s.intro);
    for (const item of s.items) parts.push(`- ${item}`);
    if (s.note) parts.push(s.note);
    parts.push("");
  }
  return parts.join("\n").trim();
}
